import { useAuth } from "@src/routes/authProvider";
import { paths } from "@src/routes/paths";
import { pricingActions, settingsActions, systemActions } from "@src/store";
import React, { useEffect } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";

import styles from "./Home.module.scss";
import { useAppDispatch, useAppSelector } from "@src/store/hooks";
import { saveReturnUrl } from "@src/utils/session.utils";
import PortalBlast from "@src/components/portalBlast/PortalBlast";
import { TopNav } from "@src/components/topNav/TopNav";
import { FiltersPanel, routeHasFilterPanel } from "@src/components/filtersPanel/FiltersPanel";
import { useGridTogglePersistence } from "@src/hooks/useGridTogglePersistence";
import { ErrorBoundary } from "@src/components/errorBoundary/ErrorBoundary";

export const Home: React.FC = () => {
	const appDispatch = useAppDispatch();
	const { isAuthenticated } = useAuth();
	const portalSidebarExpanded = useAppSelector((state) => state.settings.portalSidebarExpanded);

	useGridTogglePersistence();
	// This can hide sidebar with autologin from old portal
	// const portalSidebarIsHidden = useAppSelector((state) => state.settings.portalSidebarIsHidden);

	const isAdmin = useAppSelector((state) => state.settings.isAdmin);
	const companies = useAppSelector((state) => state.pricing.companies.data);

	useEffect(() => {
		if (!isAuthenticated || !isAdmin || companies.length) return;
		appDispatch(pricingActions.listCompanies({}));
	}, [isAuthenticated, isAdmin, appDispatch, companies.length]);

	const exportDestinationsLoaded = useAppSelector((state) => state.system.exportDestinations.data.length > 0);

	useEffect(() => {
		if (!isAuthenticated || exportDestinationsLoaded) return;
		appDispatch(systemActions.listExportDestinations({}));
	}, [isAuthenticated, appDispatch, exportDestinationsLoaded]);

	const location = useLocation();
	const hasFilterPanel = routeHasFilterPanel(location.pathname);
	const isPanelExpanded = portalSidebarExpanded && hasFilterPanel;

	if (!isAuthenticated && location.pathname !== paths.login) {
		// Persist the current deep-link (including query params) so the
		// user returns here after re-authenticating.
		saveReturnUrl();
		return <Navigate to={paths.login} replace />;
	}

	const toggleExpanded = () => {
		appDispatch(settingsActions.setPortalSidebarExpanded(!portalSidebarExpanded));
	};

	return (
		<div
			className={styles.main}
			data-is-expanded={isPanelExpanded}
			// data-is-side-bar-hidden={portalSidebarIsHidden}
		>
			<TopNav />
			<PortalBlast />
			<FiltersPanel isExpanded={isPanelExpanded} toggleExpanded={toggleExpanded} />
			<div className={styles.childRoutesContainer}>
				<main>
					<ErrorBoundary resetKey={location.pathname}>
						<Outlet />
					</ErrorBoundary>
				</main>
			</div>
		</div>
	);
};
